import { REPORT_ACCESS_PREFIX } from '../report-access';
const SESSION_COOKIE_PREFIX = 'rwss_';
const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24 * 180;

const canUseBrowserStorage = () => (
    typeof window !== 'undefined'
    && typeof document !== 'undefined'
    && typeof window.sessionStorage !== 'undefined'
);

const normalizeKey = (key) => String(key ?? '');
const normalizeValue = (value) => String(value ?? '');

const getSessionCookieName = (key) => `${SESSION_COOKIE_PREFIX}${encodeURIComponent(normalizeKey(key))}`;

const buildCookieString = (name, value, maxAge = SESSION_COOKIE_MAX_AGE) => {
    const secureFlag = window.location.protocol === 'https:' ? ';Secure' : '';
    return `${name}=${encodeURIComponent(normalizeValue(value))};Max-Age=${maxAge};Path=/;SameSite=Lax${secureFlag}`;
};

export const getCookieValueByName = (name) => {
    if (!canUseBrowserStorage()) return '';

    const cookieName = `${name}=`;
    const cookieParts = (document.cookie || '').split(';');

    for (const part of cookieParts) {
        const cookie = part.trim();
        if (cookie.startsWith(cookieName)) {
            try { return decodeURIComponent(cookie.slice(cookieName.length)); }
            catch { return ''; }
        }
    }

    return '';
};

export const setCookieValueByName = (name, value, maxAge = SESSION_COOKIE_MAX_AGE) => {
    if (!canUseBrowserStorage()) return;
    document.cookie = buildCookieString(name, value, maxAge);
};

export const removeCookieByName = (name) => {
    if (!canUseBrowserStorage()) return;
    document.cookie = `${name}=;Max-Age=0;Path=/;SameSite=Lax`;
};

export const getPersistedSessionValue = (key) => {
    if (!canUseBrowserStorage()) return '';
    if (normalizeKey(key).startsWith(REPORT_ACCESS_PREFIX)) return '';
    return getCookieValueByName(getSessionCookieName(key));
};

export const setPersistedSessionValue = (key, value) => {
    if (!canUseBrowserStorage()) return '';
    const normalizedKey = normalizeKey(key);
    const normalizedValue = normalizeValue(value);
    if (normalizedKey.startsWith(REPORT_ACCESS_PREFIX)) return normalizedValue;
    setCookieValueByName(getSessionCookieName(normalizedKey), normalizedValue);
    return normalizedValue;
};

export const removePersistedSessionValue = (key) => {
    if (!canUseBrowserStorage()) return;
    removeCookieByName(getSessionCookieName(key));
};

export const listPersistedSessionKeys = () => {
    if (!canUseBrowserStorage()) return [];

    return (document.cookie || '')
        .split(';')
        .map((entry) => entry.trim())
        .filter((entry) => entry.startsWith(SESSION_COOKIE_PREFIX))
        .map((entry) => entry.split('=')[0] || '')
        .map((name) => decodeURIComponent(name.slice(SESSION_COOKIE_PREFIX.length)))
        .filter(key => key && !key.startsWith(REPORT_ACCESS_PREFIX));
};

export const hydratePersistedSession = () => {
    if (!canUseBrowserStorage()) return [];

    const keys = listPersistedSessionKeys();
    keys.forEach((key) => {
        const cookieValue = getPersistedSessionValue(key);
        if (cookieValue !== '' && window.sessionStorage.getItem(key) === null) {
            window.sessionStorage.setItem(key, cookieValue);
        }
    });

    return keys;
};

export const clearPersistedSessionCookies = () => {
    if (!canUseBrowserStorage()) return;
    listPersistedSessionKeys().forEach((key) => removePersistedSessionValue(key));
};

export const installSessionCookieBridge = () => {
    if (!canUseBrowserStorage()) return;
    if (window.__rwSessionCookieBridgeInstalled) return;

    const originalGetItem = Storage.prototype.getItem;
    const originalSetItem = Storage.prototype.setItem;
    const originalRemoveItem = Storage.prototype.removeItem;
    const originalClear = Storage.prototype.clear;

    const isSessionStorageTarget = (storage) => storage === window.sessionStorage;

    Storage.prototype.getItem = function patchedGetItem(key) {
        if (!isSessionStorageTarget(this)) {
            return originalGetItem.call(this, key);
        }

        const normalizedKey = normalizeKey(key);
        // A tab owns its report context. Cookies may restore missing values, but a
        // different tab must never replace this tab's CID, slug, or selected dates.
        const sessionValue = originalGetItem.call(this, normalizedKey);
        if (sessionValue !== null) return sessionValue;
        const cookieValue = getPersistedSessionValue(normalizedKey);
        if (cookieValue !== '') {
            originalSetItem.call(this, normalizedKey, cookieValue);
            return cookieValue;
        }
        return null;
    };

    Storage.prototype.setItem = function patchedSetItem(key, value) {
        if (!isSessionStorageTarget(this)) {
            return originalSetItem.call(this, key, value);
        }

        const normalizedKey = normalizeKey(key);
        const normalizedValue = setPersistedSessionValue(normalizedKey, value);
        return originalSetItem.call(this, normalizedKey, normalizedValue);
    };

    Storage.prototype.removeItem = function patchedRemoveItem(key) {
        if (!isSessionStorageTarget(this)) {
            return originalRemoveItem.call(this, key);
        }

        const normalizedKey = normalizeKey(key);
        removePersistedSessionValue(normalizedKey);
        return originalRemoveItem.call(this, normalizedKey);
    };

    Storage.prototype.clear = function patchedClear() {
        if (!isSessionStorageTarget(this)) {
            return originalClear.call(this);
        }

        clearPersistedSessionCookies();
        return originalClear.call(this);
    };

    hydratePersistedSession();
    window.__rwSessionCookieBridgeInstalled = true;
};

export { SESSION_COOKIE_MAX_AGE, SESSION_COOKIE_PREFIX, getSessionCookieName };
